import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { StatCard } from '@/components/dashboard/StatCard';
import { Wrench } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  aggregateShopCapacity,
  describeCapacityCompleteness,
} from '@/utils/capacity/technicianCapacityState';
// Phase G2A — canonical live-load helpers. Shop Utilization now reflects
// live projected occupancy (including run-long + bay-blocking pause) so
// the Overview number agrees with the Tech Workload cards and the Dayboard
// tech-column header. Held-in-parking / non-bay holds correctly contribute
// 0 per Bucket 4.5 §4.5.8. Terminal/deleted/completed tickets are excluded
// at the query level AND defensively zeroed by the helper.
import { getDerivedTicketTime, sumWorkloadMinutes } from '@/utils/derivedTicketTime';
import { clock } from '@/utils/clock';
import { isOnBacklogBoard } from '@/utils/backlogState';

// DCC-A4: dropped `format` from date-fns import + added canonical
// `selectedDayLabel: string` prop. `selectedDate: Date` prop kept for
// `calculateTechCapacity(tech, selectedDate)` (DCD-A3 OUT_OF_SCOPE
// pattern: helper accepting Date is the helper's contract, not date-
// label derivation). DB-binding `dateStr` now reads canonical SoT
// directly.

interface ShopUtilizationCardProps {
  shopId: string;
  selectedDate: Date;
  selectedDayLabel: string;
}

export function ShopUtilizationCard({ shopId, selectedDate, selectedDayLabel }: ShopUtilizationCardProps) {
  const dateStr = selectedDayLabel;

  const { data, isLoading } = useQuery({
    queryKey: ['shop-utilization-card', shopId, dateStr],
    queryFn: async () => {
      // Fetch active technicians — full row needed for derivedTicketTime
      // (efficiency, buffer, breaks) and for capacity calculation.
      const { data: techs } = await supabase
        .from('technicians')
        .select('*')
        .eq('shop_id', shopId)
        .eq('employment_status', 'Active');

      const technicians = techs || [];
      const techById = new Map<string, any>(
        technicians.map((t: any) => [t.id, t]),
      );

      // WHO COUNTS. This total is divided into booked MECHANICAL minutes, so
      // a detail specialist's eight hours in the denominator make the shop
      // look half as busy as it is — and the error is invisible, because the
      // number stays plausible and moves in the right direction. The only
      // symptom is a manager who thinks there is room for another job.
      //
      // `shop_mechanical_technician_ids` is the database's answer, from the
      // same work-role model the assignment triggers use. When it cannot be
      // read the set is left null and every technician counts, which is the
      // pre-multi-role behaviour: a utilization figure that is slightly
      // optimistic is better than a card that renders nothing.
      let mechanicalTechIds: Set<string> | null = null;
      const { data: mechIds, error: mechError } = await supabase
        .rpc('shop_mechanical_technician_ids' as never, { p_shop_id: shopId } as never);
      if (mechError) {
        console.error('[shop-utilization] mechanical technician query failed:', mechError);
      } else {
        mechanicalTechIds = new Set(
          ((mechIds ?? []) as unknown[]).map((r) =>
            typeof r === 'string' ? r : String((r as { id?: string })?.id ?? r)),
        );
      }

      // CORE-AW §44. This summed a per-technician number that was 0 for a
      // technician whose hours were never recorded, so an incomplete roster
      // produced a total presented as the shop's capacity for the day. The
      // aggregate now carries whether it is complete, and excludes unknowns
      // rather than counting them as nothing.
      // B-W4 rule 5: liveWorkloadMinutes is OptiTime workload (wall minutes: book ÷ efficiency × (1 + buffer)).
      // The 'adjusted' capacity (× efficiency × (1 − buffer)) applied the buffer a second time; compare wall with wall.
      const shopCapacity = aggregateShopCapacity(
        technicians, selectedDate, mechanicalTechIds, 'scheduled',
      );
      const totalCapacity = shopCapacity.minutes;

      // Fetch scheduled tickets for this date.
      // DMC-D3a: canonical active-ticket filter — both reps excluded.
      // Phase G2A: expanded SELECT to include the fields getDerivedTicketTime
      // needs to classify production state, hold channel, and live elapsed
      // time. predicted_minutes is still the canonical book-time source.
      const { data: tickets } = await supabase
        .from('tickets')
        .select(
          'id, predicted_minutes, parking_lot, parking_lot_reason, parking_spot_id, bay_id, ' +
            'specialty_bay_id, status, timer_status, paused_at, pause_reason, ' +
            'actually_started, actual_start_time, start_time, ' +
            'run_long_minutes, run_long_opti_baseline, completed_at, ' +
            'picked_up_at, archived_at, deleted_at, assigned_tech_id, ' +
            'scheduled_tech_id, scheduled_date, scheduled_start_time, ' +
            'is_estimate, bay_hold_owner, is_explicit_bay_hold, ' +
            'due_out, vehicle_id, type',
        )
        .eq('shop_id', shopId)
        .eq('scheduled_date', dateStr)
        .not('status', 'in', '("Completed","Deleted")')
        .is('deleted_at', null);

      // Pre-filter parking_lot tickets out (Bucket 4.5 §4.5.8: non-bay
      // holds do NOT inflate bay occupancy). The helper also returns 0
      // for held-in-parking / non-bay states as defense-in-depth, but the
      // explicit filter matches the existing card semantic and the pattern
      // used by OBLIBucket / DayboardLoadIndicator (deferred to G2B).
      const eligibleTickets = (tickets || []).filter((t: any) => !isOnBacklogBoard(t));   // Stage B/F.1

      // Canonical live projected workload — same reducer pattern used by
      // TechnicianColumn.tsx and Dashboard.tsx Overview Tech Workload cards.
      const nowMs = clock.nowMs();
      const liveWorkloadMinutes = sumWorkloadMinutes(
        eligibleTickets.map((t: any) => {
          const tech = t.assigned_tech_id ? techById.get(t.assigned_tech_id) : null;
          const actualStartIso = t.actual_start_time as string | null | undefined;
          const liveElapsedSeconds = actualStartIso
            ? Math.max(0, Math.floor((nowMs - new Date(actualStartIso).getTime()) / 1000))
            : 0;
          const startIso = t.start_time as string | null | undefined;
          const startInstant = startIso ? new Date(startIso) : null;
          return getDerivedTicketTime({
            ticket: t,
            technician: tech
              ? {
                  efficiencyPercentage: tech.efficiency_percentage ?? 100,
                  bufferPercentage: resolvePlanningBufferPct(tech.buffer_percentage),
                  recurring_breaks: tech.recurring_breaks ?? null,
                  non_recurring_breaks: tech.non_recurring_breaks ?? null,
                  shift_start: tech.shift_start ?? null,
                  shift_end: tech.shift_end ?? null,
                }
              : null,
            // Jobs are not fetched here; predicted_minutes is the canonical
            // book-time source per recomputeTicketPredictedMinutes contract.
            jobs: null,
            shopConfig: { willingnessRollPastEodMin: 0, eodRiskBandMin: 30 },
            startInstant:
              startInstant && Number.isFinite(startInstant.getTime())
                ? startInstant
                : null,
            nowInstant: new Date(nowMs),
            liveElapsedSeconds,
          });
        }),
      );

      const pct =
        totalCapacity > 0
          ? Math.round((liveWorkloadMinutes / totalCapacity) * 100)
          : 0;

      return {
        pct, liveWorkloadMinutes, totalCapacity,
        capacityComplete: shopCapacity.complete,
        capacityNote: describeCapacityCompleteness(shopCapacity),
      };
    },
    enabled: Boolean(shopId),
    staleTime: 60 * 1000,
  });

  if (!shopId) return null;

  const value = isLoading ? '--' : data ? `${data.pct}%` : '--';
  // Phase G2A: subtitle reflects the new semantic. "Live load" includes
  // run-long and bay-blocking pause; non-bay holds correctly contribute 0.
  //
  // CORE-AW §44: where any technician's hours are unrecorded the denominator
  // is a partial one, and the subtitle says so. The ratio is still honest
  // arithmetic over known capacity — it stops being honest the moment it is
  // presented as the whole shop.
  const subtitle = isLoading
    ? 'Loading...'
    : data && !data.capacityComplete
      ? `Live load ÷ known capacity — ${data.capacityNote}`
      : 'Live load ÷ capacity';

  return (
    <StatCard
      title="Shop Utilization"
      value={value}
      subtitle={subtitle}
      icon={Wrench}
    />
  );
}
